import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Star } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';

export const Stars = ({ n, size = 14 }) => (
  <span className="inline-flex text-amber-400" aria-label={`${n} stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} fill={i <= Math.round(n) ? 'currentColor' : 'none'} className={i <= Math.round(n) ? '' : 'text-slate-300'} />)}
  </span>
);

const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export const GoogleReviews = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/feedback/google-reviews`, { headers: authHeader() }).then((r) => {
      setData(r.data);
      axios.post(`${API}/feedback/google-reviews/seen`, {}, { headers: authHeader() }).catch(() => {});
    }).catch(() => setData({ reviews: [] }));
  }, [authHeader]);
  if (!data) return null;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="google-reviews-list">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 p-4">
        <p className="text-xs text-slate-500">Your newest Google reviews (Google shares up to 5). Reply from your Business Profile.</p>
        <a href={data.manage_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700" data-testid="google-manage-reviews">Manage all reviews <ExternalLink size={12} /></a>
      </div>
      {data.reviews.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No Google reviews to show. Check the Place ID in Settings.</p>}
      {data.reviews.map((r, i) => {
        const isNew = !data.seen_at || new Date(r.time) > new Date(data.seen_at);
        return (
          <div key={`${r.time}-${i}`} className="border-b border-slate-100 p-4 last:border-0" data-testid={`google-review-${i}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {r.photo && <img src={r.photo} alt="" className="h-7 w-7 rounded-full" referrerPolicy="no-referrer" />}
                <span className="text-sm font-semibold text-slate-900">{r.author}</span>
                <Stars n={r.rating} />
                {isNew && <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold uppercase text-teal-700" data-testid={`google-review-new-${i}`}>New</span>}
              </div>
              <span className="text-xs text-slate-400">{r.relative || day(r.time)}</span>
            </div>
            {r.text && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{r.text}</p>}
            {r.url && (
              <a href={r.url} target="_blank" rel="noreferrer" data-testid={`google-review-reply-${i}`}
                className="mt-2 inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:border-teal-500 hover:text-teal-700">
                Reply on Google <ExternalLink size={12} />
              </a>
            )}
          </div>
        );
      })}
      <p className="px-4 pb-3 pt-1 text-[10px] text-slate-400">Reviews from Google</p>
    </div>
  );
};

export const TechLeaderboard = () => {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/feedback/technicians`, { headers: authHeader() }).then((r) => setRows(r.data.technicians)).catch(() => setRows([]));
  }, [authHeader]);
  if (!rows) return null;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="tech-leaderboard">
      {rows.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No ratings linked to technicians yet. Add who worked on each job card or invoice.</p>}
      {rows.map((t, i) => (
        <div key={t.staff_id} className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 p-4 last:border-0" data-testid={`tech-row-${t.staff_id}`}>
          <div className="flex items-center gap-3">
            <span className="w-5 text-sm font-semibold text-slate-400">{i + 1}</span>
            <div>
              <p className="text-sm font-semibold text-slate-900">{t.name}</p>
              <p className="text-xs text-slate-500">{t.role}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center justify-end gap-2"><Stars n={t.average} /><span className="text-sm font-semibold" data-testid={`tech-avg-${t.staff_id}`}>{t.average}</span></div>
            <p className="text-xs text-slate-500">{t.count} rating{t.count === 1 ? '' : 's'}</p>
          </div>
          {t.latest_low && (
            <p className="w-full rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid={`tech-low-${t.staff_id}`}>
              Latest low rating: {t.latest_low.rating}★ · {t.latest_low.invoice_number} · {day(t.latest_low.rated_at)}{t.latest_low.comment ? ` · “${t.latest_low.comment}”` : ''}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

// Shared fetch for staff list chips: one call, keyed by staff id.
export const useTechRatings = () => {
  const { authHeader } = useAuth();
  const [map, setMap] = useState({});
  useEffect(() => {
    axios.get(`${API}/feedback/technicians`, { headers: authHeader() })
      .then((r) => setMap(Object.fromEntries(r.data.technicians.map((t) => [t.staff_id, t])))).catch(() => {});
  }, [authHeader]);
  return map;
};

export const RatingChip = ({ t, testid }) => (t ? (
  <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-amber-600" data-testid={testid}>
    <Star size={11} fill="currentColor" /> {t.average} · {t.count} rating{t.count === 1 ? '' : 's'}
  </span>
) : null);

const RatingCard = ({ title, avg, count, extra, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-slate-500">{title}</div>
    {count ? (
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-slate-900">{avg}</span><Stars n={avg} />
        <span className="text-xs text-slate-500">from {count} customer rating{count === 1 ? '' : 's'}</span>
      </div>
    ) : <p className="mt-1 text-sm text-slate-500">No customer ratings yet for jobs you worked on.</p>}
    {extra}
  </div>
);

const BonusProgress = ({ b }) => {
  const earned = b.count >= b.min_count && (b.average || 0) >= b.min_avg;
  const more = Math.max(0, b.min_count - b.count);
  const amt = `₹${Number(b.amount).toLocaleString('en-IN')}`;
  const text = earned ? `${b.average}★ from ${b.count} ratings this month: you've earned the ${amt} rating bonus!`
    : b.count === 0 ? `No ratings yet this month: get ${b.min_count} ratings at ${b.min_avg}★+ to earn ${amt}`
      : more > 0 ? `${b.average}★ from ${b.count} rating${b.count === 1 ? '' : 's'} this month: ${more} more rating${more === 1 ? '' : 's'} at ${b.min_avg}★+ to earn ${amt}`
        : `${b.average}★ from ${b.count} ratings this month: lift your average to ${b.min_avg}★ to earn ${amt}`;
  const pct = earned ? 100 : Math.min(100, Math.round((Math.min(b.count, b.min_count) / b.min_count) * ((b.average || 0) >= b.min_avg || !b.count ? 100 : 80)));
  return (
    <div className="mt-3" data-testid="my-bonus-progress">
      <p className={`text-xs font-semibold ${earned ? 'text-emerald-700' : 'text-slate-700'}`} data-testid="my-bonus-text">{text}</p>
      <div className="mt-1.5 h-2 rounded-full bg-slate-100"><div className={`h-2 rounded-full ${earned ? 'bg-emerald-500' : 'bg-amber-400'}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

export const MyRatingCard = () => {
  const { authHeader } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/feedback/my-rating`, { headers: authHeader() }).then((r) => setD(r.data)).catch(() => {});
  }, [authHeader]);
  if (!d) return null;
  return <RatingCard title="Customer rating for your jobs" avg={d.average} count={d.count} testid="my-customer-rating" extra={d.bonus && <BonusProgress b={d.bonus} />} />;
};

export const StaffRatingCard = ({ staffId }) => {
  const { authHeader } = useAuth();
  const [t, setT] = useState(undefined);
  useEffect(() => {
    if (!staffId) return;
    axios.get(`${API}/feedback/technicians`, { headers: authHeader(), params: { staff_id: staffId } })
      .then((r) => setT(r.data.technicians[0] || null)).catch(() => setT(undefined));
  }, [authHeader, staffId]);
  if (t === undefined) return null;
  return (
    <RatingCard title="Customer rating" avg={t?.average} count={t?.count} testid="staff-customer-rating"
      extra={t?.latest_low && <p className="mt-2 text-xs text-rose-700">Latest low: {t.latest_low.rating}★ · {t.latest_low.invoice_number}{t.latest_low.comment ? ` · “${t.latest_low.comment}”` : ''}</p>} />
  );
};
